import type {DefinitionOrReference} from '@github-ui/code-nav'
import {usePath} from '@github-ui/code-view-shared/contexts/PathContext'
import {useReposAnalytics} from '@github-ui/code-view-shared/hooks/use-repos-analytics'
import {type Repository, useCurrentRepository} from '@github-ui/current-repository'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {ChevronDownIcon, ChevronRightIcon, FoldIcon, UnfoldIcon} from '@primer/octicons-react'
import {Button, CounterLabel, Truncate} from '@primer/react'
import {clsx} from 'clsx'
import React, {useCallback, useEffect, useState} from 'react'

import {useCurrentBlame} from '../../../../hooks/CurrentBlame'
import {useKeyboardNavUsed} from '../../../../hooks/use-cursor-navigation'
import {textAreaId} from '../../../../utilities/lines'
import {CodeNavCell, focusSibling} from './CodeNavCell'
import styles from './CodeNavFileInformation.module.css'

const MaxReferences = 10

export interface CodeNavFileInformationProps {
  results: DefinitionOrReference[]
  repo: Repository
  filePath: string
  highlightedIndex?: number
  isDefinition?: boolean
  onClick?: (index: number) => void
  offset: number
  initiallyExpanded: boolean
  enableExpandCollapse: boolean
  symbol: string
  setFocusOnFile?: boolean
}

export function CodeNavFileInformation({
  results,
  repo,
  filePath,
  highlightedIndex,
  isDefinition,
  onClick,
  // The offset counts how many references are above this one
  offset,
  initiallyExpanded,
  enableExpandCollapse,
  symbol,
  setFocusOnFile,
}: CodeNavFileInformationProps) {
  const hasBlame = !!useCurrentBlame()
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded)
  const currentRepo = useCurrentRepository()
  const currentPath = usePath()
  const firstFileRef = React.useRef<HTMLDivElement>(null)

  const [showAllRefs, setShowAllRefs] = useState(false)

  const keyboardNavUsed = useKeyboardNavUsed()

  const initialVisibleRefs = results.slice(0, MaxReferences)
  const hiddenRefs = results.length > MaxReferences ? results.slice(MaxReferences) : []

  const {sendRepoClickEvent} = useReposAnalytics()
  const isFromCurrentRepo = currentRepo.ownerLogin === repo.ownerLogin && currentRepo.name === repo.name

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLAnchorElement | HTMLButtonElement | HTMLDivElement>) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.key === 'Enter' || e.key === ' ') {
        setIsExpanded(prev => !prev)
        e.preventDefault()
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      } else if (e.key === 'ArrowLeft') {
        setIsExpanded(false)
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      } else if (e.key === 'ArrowRight') {
        setIsExpanded(true)
        if (isExpanded) {
          firstFileRef.current?.focus()
        }
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      } else if (e.key === 'ArrowDown') {
        focusSibling('nextElementSibling')
        e.preventDefault()
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      } else if (e.key === 'ArrowUp') {
        focusSibling('previousElementSibling')
        e.preventDefault()
      }
    },
    [isExpanded],
  )

  useEffect(() => {
    if (highlightedIndex && highlightedIndex >= MaxReferences + offset) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShowAllRefs(true)
    }
  }, [highlightedIndex, offset])

  useEffect(() => {
    if (setFocusOnFile && keyboardNavUsed) {
      firstFileRef.current?.focus()
    }
  }, [setFocusOnFile, keyboardNavUsed])

  useEffect(() => {
    //this effect will always run after the above one, so we will focus back to the text area if necessary
    if (!keyboardNavUsed) {
      document.getElementById(textAreaId)?.focus()
    }
  }, [keyboardNavUsed])

  const groupId = `${filePath}-${isDefinition ? 'definition' : 'reference'}-group`

  return (
    <div key={filePath}>
      {}
      <div
        className={clsx(styles.fileHeader, enableExpandCollapse && styles.fileHeaderExpandable)}
        onClick={enableExpandCollapse ? () => setIsExpanded(prev => !prev) : undefined}
        onKeyDown={onKeyDown}
        ref={firstFileRef}
        role={enableExpandCollapse ? 'button' : undefined}
        tabIndex={0}
      >
        <div className={styles.flexRow}>
          {isDefinition && currentRepo.id !== repo.id && (
            <>
              <GitHubAvatar square src={repo.ownerAvatar} className={styles.repoAvatar} size={16} />
              <div className={styles.repoName}>{repo.name}</div>
            </>
          )}
          <button aria-expanded={isExpanded} aria-controls={groupId} className={styles.expandButton}>
            {enableExpandCollapse &&
              (isExpanded ? <ChevronDownIcon aria-hidden="true" /> : <ChevronRightIcon aria-hidden="true" />)}
            {/* display table to fill and fix to 100% content - needed for display table-cell */}
            <div className={styles.tableWrapper}>
              <Truncate
                aria-label={`${isDefinition ? 'Definitions' : 'References'} in ${
                  filePath !== currentPath ? filePath : 'this file'
                }`}
                title={filePath}
                className={styles.truncatedPath}
              >
                &lrm;{!isFromCurrentRepo || filePath !== currentPath ? filePath : 'In this file'}&lrm;
              </Truncate>
            </div>
          </button>
        </div>
        {results && !isExpanded && <CounterLabel className={styles.counterLabel}>{results.length}</CounterLabel>}
      </div>
      {isExpanded && (
        <div
          aria-label={`Results in ${filePath !== currentPath ? filePath : 'this file'}`}
          id={groupId}
          className={clsx(styles.resultsGroup, 'code-nav-file-information')}
          role="group"
        >
          {initialVisibleRefs.map((reference, i) => {
            return (
              <CodeNavCell
                key={`codeNavigation${i + offset}`}
                reference={reference}
                isHighlighted={highlightedIndex === i + offset}
                href={reference.href(hasBlame)}
                onClick={() => {
                  if (onClick) onClick(i + offset)
                  sendRepoClickEvent('BLOB_SYMBOLS_MENU.SYMBOL_DEFINITION_CLICK')
                }}
                symbol={symbol}
                index={i + offset}
              />
            )
          })}

          {showAllRefs &&
            hiddenRefs.map((reference, i) => {
              return (
                <CodeNavCell
                  key={`codeNavigation${i + offset + MaxReferences}`}
                  reference={reference}
                  isHighlighted={highlightedIndex === i + offset + MaxReferences}
                  href={reference.href(hasBlame)}
                  onClick={() => {
                    if (onClick) onClick(i + offset + MaxReferences)
                    sendRepoClickEvent('BLOB_SYMBOLS_MENU.SYMBOL_DEFINITION_CLICK')
                  }}
                  symbol={symbol}
                  index={i + offset + MaxReferences}
                  focusElement={i === 0}
                />
              )
            })}

          {hiddenRefs.length > 0 && (
            <div className={styles.showMoreWrapper}>
              <Button
                leadingVisual={showAllRefs ? FoldIcon : UnfoldIcon}
                onClick={() => setShowAllRefs(prev => !prev)}
                className={styles.showMoreButton}
                variant="invisible"
                size="small"
                aria-selected={false}
              >
                {showAllRefs ? 'Show less' : 'Show more'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
